import { useCallback, useEffect, useRef, useState } from 'react';
import type { Character } from '../shared/types';
import type { EmotePreset } from '../shared/emotes';
import { post } from './api';
import { RoomSession, type RoomSessionState, type FriendMeetingRequest } from './room-session';
export type { FriendMeetingRequest, FriendMeetingResult } from './room-session';

const initialState = (): RoomSessionState => ({
  peers: [],
  emotes: {},
  status: 'connecting',
  roomId: '',
  newsEpoch: 0,
});
export function useRoom(character: Character | undefined, meeting?: FriendMeetingRequest) {
  const latest = useRef(character);
  latest.current = character;
  const controller = useRef<RoomSession | undefined>(undefined);
  const lastMeeting = useRef('');
  const [state, setState] = useState<RoomSessionState>(initialState);
  useEffect(() => {
    setState(initialState());
    if (!character) {
      controller.current = undefined;
      return;
    }
    const session = new RoomSession(character, {
      character: () => latest.current,
      post,
      client: async (endpoint) => {
        const { Client } = await import('@colyseus/sdk');
        return new Client(endpoint);
      },
      storage: sessionStorage,
      sessionEnded: (restricted) =>
        window.dispatchEvent(new CustomEvent('ado-session-ended', { detail: { restricted } })),
      changed: setState,
    });
    controller.current = session;
    const ended = () => session.dispose();
    window.addEventListener('ado-session-ended', ended);
    session.start();
    return () => {
      window.removeEventListener('ado-session-ended', ended);
      if (controller.current === session) controller.current = undefined;
      session.dispose();
    };
  }, [character?.id, character?.cityId]);
  // A new meeting attempt is independent of the identity lifecycle. It cannot
  // tear down a healthy source room while a reservation is being prepared.
  useEffect(() => {
    if (!character || !meeting || !controller.current) return;
    const key = `${character.id}:${character.cityId}:${meeting.targetId}:${meeting.attempt}`;
    // Closing an account/privacy panel recreates the room lifecycle. It must not
    // replay an already issued meeting intent without another user attempt.
    if (lastMeeting.current === key) return;
    lastMeeting.current = key;
    controller.current.meeting(meeting);
  }, [character?.id, character?.cityId, meeting?.targetId, meeting?.attempt]);
  const sendEmote = useCallback((preset: EmotePreset) => controller.current?.sendEmote(preset), []);
  return { ...state, sendEmote };
}
